import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ringsFromGeocercaItem } from "./mapa-geocercas-cliente";
import { obtenerHistorico } from "../api/api-seguimiento";
import moment from "moment";

const DEFAULT_CENTER = { lat: -17.78, lng: -63.18 };
const MAPS_POLL_MS = 150;
const MAPS_POLL_MAX_MS = 8000;
const PATH_STROKE_RED = "#dc2626";
const MARKER_START_COLOR = "#15803d";
const MARKER_END_COLOR = "#dc2626";
/** Pixel spacing between direction arrows along the historic path (Google Maps symbol repeat). */
const PATH_ARROW_REPEAT = "96px";

/**
 * @param {unknown} s
 */
function escapeHtml(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/**
 * @param {unknown} v
 */
function formatFechaHoraHistorico(v) {
    if (v == null || v === "") return "—";
    const m = moment(v);
    return m.isValid() ? m.format("YYYY-MM-DD HH:mm:ss") : escapeHtml(v);
}

/**
 * @param {object} row
 * @param {"start"|"end"} role
 * @param {function} t — i18next `t`
 */
function buildHistoricInfoHtml(row, role, t) {
    const title = role === "start" ? t("label_inicio") : t("label_fin");
    const placa = row.placa != null ? String(row.placa) : "—";
    const vel = row.velocidad;
    const velStr =
        vel != null && vel !== "" && !Number.isNaN(Number(vel)) ? `${Number(vel)} km/h` : "—";
    const evento =
        row.tipoEventoLiteral != null && String(row.tipoEventoLiteral).trim() !== ""
            ? String(row.tipoEventoLiteral)
            : "—";
    // const conductor =
    //     row.nombreConductor != null && String(row.nombreConductor).trim() !== ""
    //         ? String(row.nombreConductor)
    //         : "—";

    return (
        `<div style="font:12px/1.45 system-ui,-apple-system,sans-serif;min-width:200px;max-width:280px;color:#111827">` +
        `<div style="font-weight:700;margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #e5e7eb">${escapeHtml(
            title
        )}</div>` +
        `<div style="display:grid;grid-template-columns:auto 1fr;gap:4px 10px;align-items:start">` +
        `<span style="color:#6b7280">${escapeHtml(t("label_fecha"))}</span>` +
        `<span>${escapeHtml(formatFechaHoraHistorico(row.fechaHora))}</span>` +
        `<span style="color:#6b7280">${escapeHtml(t("label_placa"))}</span>` +
        `<span>${escapeHtml(placa)}</span>` +
        `<span style="color:#6b7280">${escapeHtml(t("label_velocidad"))}</span>` +
        `<span>${escapeHtml(velStr)}</span>` +
        `<span style="color:#6b7280">${escapeHtml(t("label_tipo_evento"))}</span>` +
        `<span>${escapeHtml(evento)}</span>` +
        // `<span style="color:#6b7280">${escapeHtml(t("label_nombre_conductor"))}</span>` +
        // `<span>${escapeHtml(conductor)}</span>` +
        `</div></div>`
    );
}

/**
 * @param {unknown} raw
 * @returns {Array<object & { lat: number, lng: number }>}
 */
function normalizePathRows(raw) {
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const p of raw) {
        if (p == null || typeof p !== "object") continue;
        const lat = parseFloat(p.lat ?? p.Lat ?? p.latitud ?? p.latitude);
        const lng = parseFloat(p.lng ?? p.Lng ?? p.longitud ?? p.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
            continue;
        }
        out.push({ ...p, lat, lng });
    }
    return out;
}

/**
 * Google Map: geofence polygons from `geocercas`, plus a red path polyline from
 * `obtenerHistorico` when the component mounts / query params change.
 *
 * @param {Array<object>} geocercas
 * @param {string|number|null} [idGrupoGeocerca]
 * @param {string} [fechaEntrega]
 * @param {string} [valuePlaca]
 * @param {string|number|null} [idEmpresa]
 * @param {string} [className]
 * @param {string} [minHeightClass]
 */
export default function MapaGeocercasEntradaSalida({
    geocercas = [],
    idGrupoGeocerca = null,
    fechaEntrega = "",
    valuePlaca = "",
    idEmpresa = null,
    className = "",
    minHeightClass = "h-[min(50vh,420px)] min-h-[280px]",
}) {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const polygonsRef = useRef([]);
    const polylineRef = useRef(null);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);
    const pathMapClickListenerRef = useRef(null);
    const [mapsError, setMapsError] = useState(false);
    /** Full historic rows (lat/lng + API fields) for polyline and start/end markers. */
    const [pathRows, setPathRows] = useState([]);

    useEffect(() => {
        let cancelled = false;

        const loadPath = async () => {
            if (
                idGrupoGeocerca == null ||
                fechaEntrega === "" ||
                valuePlaca === "" ||
                valuePlaca == null
            ) {
                setPathRows([]);
                return;
            }
            try {
                let fechaHoraIni = moment(fechaEntrega).format('DD/MM/YYYY' + ' 00:00:00');
                let fechaHoraFin = moment(fechaEntrega).format('DD/MM/YYYY' + ' 23:59:59');

                const responseHistorico = await obtenerHistorico({
                    fechaHoraIni: fechaHoraIni,
                    fechaHoraFin: fechaHoraFin,
                    placa: valuePlaca,
                });
                if (cancelled) return;
                if (responseHistorico.error === false && responseHistorico.data != null) {
                    const raw = responseHistorico.data;
                    setPathRows(normalizePathRows(Array.isArray(raw) ? raw : []));
                } else {
                    setPathRows([]);
                }
            } catch {
                if (!cancelled) setPathRows([]);
            }
        };

        loadPath();
        return () => {
            cancelled = true;
        };
    }, [idGrupoGeocerca, fechaEntrega, valuePlaca, idEmpresa]);

    useEffect(() => {
        let cancelled = false;
        let pollTimer = null;
        const start = Date.now();

        const draw = () => {
            if (cancelled || !containerRef.current) return;
            if (!window.google?.maps) {
                if (Date.now() - start > MAPS_POLL_MAX_MS) {
                    setMapsError(true);
                    return;
                }
                pollTimer = window.setTimeout(draw, MAPS_POLL_MS);
                return;
            }

            setMapsError(false);

            if (!mapRef.current) {
                mapRef.current = new window.google.maps.Map(containerRef.current, {
                    center: DEFAULT_CENTER,
                    zoom: 12,
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                    zoomControl: true,
                });
            }

            const map = mapRef.current;
            polygonsRef.current.forEach((p) => p.setMap(null));
            polygonsRef.current = [];
            if (polylineRef.current) {
                polylineRef.current.setMap(null);
                polylineRef.current = null;
            }
            markersRef.current.forEach((m) => m.setMap(null));
            markersRef.current = [];
            if (infoWindowRef.current) {
                infoWindowRef.current.close();
            }

            const bounds = new window.google.maps.LatLngBounds();
            let hasPoint = false;

            const list = Array.isArray(geocercas) ? geocercas : [];
            list.forEach((item) => {
                const color = item.colorGeocerca || "#3388ff";
                const rings = ringsFromGeocercaItem(item);

                rings.forEach((path) => {
                    path.forEach((pt) => {
                        bounds.extend(pt);
                        hasPoint = true;
                    });
                    const polygon = new window.google.maps.Polygon({
                        paths: path,
                        strokeColor: color,
                        strokeOpacity: 0.85,
                        strokeWeight: 2,
                        fillColor: color,
                        fillOpacity: 0.28,
                        map,
                    });
                    polygonsRef.current.push(polygon);
                });
            });

            const pathLatLng =
                pathRows.length > 0
                    ? pathRows.map((r) => ({ lat: r.lat, lng: r.lng }))
                    : [];

            if (pathLatLng.length >= 2) {
                pathLatLng.forEach((pt) => {
                    bounds.extend(pt);
                    hasPoint = true;
                });
                const maps = window.google.maps;
                const polyline = new maps.Polyline({
                    path: pathLatLng,
                    geodesic: true,
                    strokeColor: PATH_STROKE_RED,
                    strokeOpacity: 1,
                    strokeWeight: 3,
                    icons: [
                        {
                            icon: {
                                path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                scale: 2.75,
                                strokeColor: PATH_STROKE_RED,
                                strokeWeight: 1.5,
                                fillColor: PATH_STROKE_RED,
                                fillOpacity: 1,
                            },
                            offset: "0",
                            repeat: PATH_ARROW_REPEAT,
                        },
                    ],
                    map,
                });
                polylineRef.current = polyline;

                if (!infoWindowRef.current) {
                    infoWindowRef.current = new maps.InfoWindow();
                }

                if (pathMapClickListenerRef.current == null) {
                    pathMapClickListenerRef.current = map.addListener("click", () => {
                        if (infoWindowRef.current) {
                            infoWindowRef.current.close();
                        }
                    });
                }

                const addHistoricMarker = (row, role) => {
                    const marker = new maps.Marker({
                        position: { lat: row.lat, lng: row.lng },
                        map,
                        icon: {
                            path: maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: role === "start" ? MARKER_START_COLOR : MARKER_END_COLOR,
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeWeight: 2,
                        },
                        zIndex: role === "end" ? 3 : 2,
                        title: role === "start" ? t("label_inicio") : t("label_fin"),
                    });
                    marker.addListener("click", () => {
                        if (infoWindowRef.current) {
                            infoWindowRef.current.setContent(buildHistoricInfoHtml(row, role, t));
                            infoWindowRef.current.open({ map, anchor: marker });
                        }
                    });
                    markersRef.current.push(marker);
                };

                addHistoricMarker(pathRows[0], "start");
                if (pathRows.length >= 2) {
                    addHistoricMarker(pathRows[pathRows.length - 1], "end");
                }
            } else if (pathLatLng.length === 1) {
                bounds.extend(pathLatLng[0]);
                hasPoint = true;
                const maps = window.google.maps;
                if (!infoWindowRef.current) {
                    infoWindowRef.current = new maps.InfoWindow();
                }
                if (pathMapClickListenerRef.current == null) {
                    pathMapClickListenerRef.current = map.addListener("click", () => {
                        if (infoWindowRef.current) {
                            infoWindowRef.current.close();
                        }
                    });
                }
                const row = pathRows[0];
                const marker = new maps.Marker({
                    position: { lat: row.lat, lng: row.lng },
                    map,
                    icon: {
                        path: maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: MARKER_START_COLOR,
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeWeight: 2,
                    },
                    zIndex: 2,
                    title: t("label_inicio"),
                });
                marker.addListener("click", () => {
                    if (infoWindowRef.current) {
                        infoWindowRef.current.setContent(buildHistoricInfoHtml(row, "start", t));
                        infoWindowRef.current.open({ map, anchor: marker });
                    }
                });
                markersRef.current.push(marker);
            }

            if (hasPoint && !bounds.isEmpty()) {
                map.fitBounds(bounds, 56);
            }
        };

        draw();

        return () => {
            cancelled = true;
            if (pollTimer) window.clearTimeout(pollTimer);
        };
    }, [geocercas, pathRows, t]);

    useEffect(() => {
        return () => {
            polygonsRef.current.forEach((p) => p.setMap(null));
            polygonsRef.current = [];
            if (polylineRef.current) {
                polylineRef.current.setMap(null);
                polylineRef.current = null;
            }
            markersRef.current.forEach((m) => m.setMap(null));
            markersRef.current = [];
            if (infoWindowRef.current) {
                infoWindowRef.current.close();
                infoWindowRef.current = null;
            }
            if (pathMapClickListenerRef.current != null) {
                window.google?.maps?.event?.removeListener(pathMapClickListenerRef.current);
                pathMapClickListenerRef.current = null;
            }
            mapRef.current = null;
        };
    }, []);

    return (
        <div className={`relative w-full ${minHeightClass} ${className}`}>
            <div
                ref={containerRef}
                className="h-full w-full rounded-lg border border-gray-200 bg-gray-100"
            />
            {mapsError && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/90 p-4 text-center text-sm text-gray-600">
                    {t("label_error_carga_mapa")}
                </div>
            )}
        </div>
    );
}
